<template>
  <!-- <div> et non <section> : le CSS global donne 100px de padding aux <section> -->
  <div class="accordion-section" :class="{'is-open': isOpen, 'is-done': isDone}" role="group" :aria-label="title">
    <button type="button" class="accordion-head" :aria-expanded="isOpen" :aria-controls="bodyId" @click="toggle()">
      <span class="accordion-step" aria-hidden="true">
        <svg v-if="isDone && !isOpen" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
        <template v-else>{{ step }}</template>
      </span>
      <span class="accordion-titles">
        <span class="accordion-title">{{ title }}</span>
        <span v-if="summary && !isOpen" class="accordion-summary" :title="summary">{{ summary }}</span>
      </span>
      <span v-if="!isOpen && isDone" class="accordion-action">Modifier</span>
      <span v-else-if="!isOpen && isOptional" class="accordion-optional">Facultatif</span>
    </button>
    <!-- v-show et non v-if : les champs d'une section repliée restent montés (valeurs, recherche d'adresse) -->
    <div v-show="isOpen" :id="bodyId" class="accordion-body">
      <div class="accordion-fields">
        <slot></slot>
      </div>
      <div v-if="nextCallback" class="accordion-next">
        <button type="button" class="accordion-next-button" :disabled="isNextDisabled" @click="nextCallback()">Continuer</button>
      </div>
    </div>
  </div>
</template>

<script>
let nextId = 0;

/**
 * Section d'un formulaire en accordéon : un seul bloc ouvert à la fois (c'est le parent qui décide, via isOpen).
 * L'en-tête indique le numéro de l'étape (une coche une fois la section terminée) et un résumé quand elle est repliée.
 * Un champ pleine largeur reçoit la classe "span-2".
 * Props : step (numéro), title, summary, isOpen, isDone, isOptional,
 *         toggleCallback (fonction appelée au clic sur l'en-tête), nextCallback (fonction du bouton « Continuer », absent = pas de bouton), isNextDisabled.
 */
export default {
  name: 'ClientAccordionSection',
  props: {
    step: {type: Number, required: true},
    title: {type: String, required: true},
    summary: {type: String, default: ''},
    isOpen: {type: Boolean, default: false},
    isDone: {type: Boolean, default: false},
    isOptional: {type: Boolean, default: false},
    toggleCallback: {type: Function, default: null},
    nextCallback: {type: Function, default: null},
    isNextDisabled: {type: Boolean, default: false},
  },
  data() {
    return {
      bodyId: `accordion-body-${nextId++}`
    };
  },
  methods: {
    toggle() {
      if (this.toggleCallback) { this.toggleCallback() }
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.accordion-section
  margin-bottom 8px
  background white
  border 1px solid PAGE_RULE_SOFT
  border-radius CTRL_RADIUS_LG
  transition border-color CTRL_EASE
  &.is-open
    border 2px solid THEME_COLOR_PRIMARY
    // le cadre de 2px ne doit pas décaler le contenu
    margin -1px -1px 7px

.accordion-head
  display flex
  align-items center
  gap 14px
  width 100%
  min-height 50px
  padding 0 16px
  font inherit
  text-align left
  color FONT_COLOR
  background transparent
  border none
  border-radius CTRL_RADIUS_LG
  cursor pointer
  &:focus-visible
    outline 2px solid CTRL_FOCUS
    outline-offset 2px

.accordion-step
  display flex
  align-items center
  justify-content center
  flex none
  width 30px
  height 30px
  border-radius 50%
  font-size 13px
  font-weight 800
  background rgba(THEME_COLOR_PRIMARY, 0.09)
  color CTRL_LABEL
  .is-done &
    background THEME_COLOR_PRIMARY
    color THEME_COLOR_PRIMARY_OPPOSITE
  // Le jaune indique où l'on en est dans le formulaire : la section en cours
  .is-open &
    background PAGE_LEMON_HOVER
    color FONT_COLOR
    border 1.5px solid PAGE_LEMON

.accordion-titles
  display flex
  align-items baseline
  flex-wrap wrap
  flex 1
  min-width 0
  gap 0 12px

.accordion-title
  font-size 1em
  font-weight 700

.accordion-summary
  flex 1 1 0
  min-width 0
  overflow hidden
  text-overflow ellipsis
  white-space nowrap
  font-size 0.88em
  color CTRL_TEXT_MUTED

.accordion-action
  flex none
  padding 4px 8px
  font-size 0.9em
  font-weight 700
  color THEME_COLOR_PRIMARY

.accordion-optional
  flex none
  font-size 12px
  font-weight 600
  color CTRL_PLACEHOLDER

.accordion-body
  padding 0 16px 10px 60px

.accordion-fields
  display grid
  grid-template-columns repeat(2, minmax(0, 1fr))
  column-gap 16px
  row-gap 2px
  :slotted(.span-2)
    grid-column 1 / -1

.accordion-next
  display flex
  justify-content flex-end
  margin-top 6px

.accordion-next-button
  height CTRL_HEIGHT
  padding 0 22px
  font inherit
  font-weight 700
  color THEME_COLOR_PRIMARY
  background white
  border 1px solid CTRL_BORDER
  border-radius CTRL_RADIUS
  cursor pointer
  &:hover:not(:disabled)
    border-color CTRL_BORDER_HOVER
    background CTRL_BG_HOVER
  &:disabled
    color CTRL_DISABLED_TEXT
    background CTRL_DISABLED_BG
    cursor not-allowed

@media only screen and (max-width: TABLET_SIZE)
  .accordion-body
    padding-left 16px
  .accordion-fields
    grid-template-columns minmax(0, 1fr)
  .accordion-summary
    flex-basis 100%
</style>
